// frontend/src/components/TimelineForm.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, TextField, Typography, Grid, Select, MenuItem, FormControl,
    InputLabel, FormControlLabel, Switch, FormHelperText,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, addMonths, isValid } from 'date-fns';

// --- Enums ---
const ApplicationStreamEnum = [
    "Provincial Nominee Program", "French language proficiency", "Canadian Experience Class",
    "Healthcare occupations", "Trade occupations", "General", "STEM occupations",
    "Transport occupations", "Agriculture and agri-food occupations", "No Program Specified",
    "Federal Skilled Worker", "Federal Skilled Trades"
];

const ProvincesEnum = [
    "Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador",
    "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan",
    "Northwest Territories", "Nunavut", "Yukon"
];

const VisaOfficeEnum = [
    "Sydney, NS",
    "Ottawa, ON",
    "Mississauga, ON",
    "Edmonton, AB",
    "Vancouver, BC",
    "Other/Not Listed" // Added an option for flexibility
];

// --- Helper Date Functions ---
const isDateValid = (date) => date && date instanceof Date && isValid(date);

const addDaysSafely = (date, days) => {
    if (!isDateValid(date)) return null;
    try {
        const resultDate = addDays(date, days);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return resultDate > today ? null : resultDate;
    } catch (error) { console.error("Error adding days:", error); return null; }
};

const addWeeksSafely = (date, weeks) => addDaysSafely(date, weeks * 7);

const addMonthsSafely = (date, months) => {
    if (!isDateValid(date)) return null;
    try {
        let resultDate;
        if (months === 2.5) {
             resultDate = addDays(date, 75); // Approx 2.5 months
        } else {
             resultDate = addMonths(date, Math.round(months));
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return resultDate > today ? null : resultDate;
    } catch (error) { console.error("Error adding months:", error); return null; }
};

// --- ADDED: Helper to parse incoming dates for DatePicker ---
// Converts null, undefined, invalid dates, or epoch dates to undefined
// Returns valid Date object otherwise
const parseIncomingDate = (raw) => {
  if (!raw) return undefined; // Handles null/undefined
  const d = new Date(raw);
  if (!isValid(d)) return undefined; // Handles invalid dates
  return d.getTime() === 0 ? undefined : d; // Handles epoch date
};
// --- End Added ---

// Define the set of fields that are targets of the auto-fill logic
const AUTOFILL_TARGET_FIELDS = new Set([
    'applicationSubmittedDate',
    'aorDate',
    'biometricsInstructionLetterDate',
    'biometricsSubmittedDate',
    'backgroundCheckStartDate', // Represents BG Completion Date
    'finalDecisionDate',
    'portal1Date',
    'portal2Date',
    'eCoprDate'
]);

// --- Component ---
export const TimelineForm = ({ formData, onChange }) => {
    const [enableAutofill, setEnableAutofill] = useState(false);

    // --- Event Handlers ---
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        onChange({ target: { name, value } });
    };

    const handleSwitchChange = (event) => {
        const { name, checked } = event.target;
        let changes = { [name]: checked };
        if (name === 'isExpressEntry' && checked) changes['isPnp'] = false;
        else if (name === 'isPnp' && checked) changes['isExpressEntry'] = false;
        Object.entries(changes).forEach(([fieldName, fieldValue]) => {
            onChange({ target: { name: fieldName, value: fieldValue } });
        });
    };

    // *** MODIFIED handleDateChange ***
    const handleDateChange = (name, date) => {
        const validDate = isDateValid(date) ? date : null;

        // If a user clears one of the auto-fillable milestone dates,
        // automatically disable the auto-fill toggle.
        if (validDate === null && AUTOFILL_TARGET_FIELDS.has(name)) {
            console.log(`User cleared '${name}', disabling auto-fill.`);
            setEnableAutofill(false);
        }

        // Always propagate the change up to the parent state
        onChange({ target: { name, value: validDate } });
    };

    // --- Auto-filling Logic ---
    const calculateDependentDates = useCallback((currentFormData) => {
       // ... (calculation logic remains the same as previous version)
       const itaDate = currentFormData.itaDate;
       const securityCheck = currentFormData.securityCheck || false;
       const updates = {};

       if (!isDateValid(itaDate)) return {};

       let calculatedAppSubmittedDate = currentFormData.applicationSubmittedDate;
       if (!isDateValid(calculatedAppSubmittedDate)) {
           updates.applicationSubmittedDate = addWeeksSafely(itaDate, 1);
           calculatedAppSubmittedDate = updates.applicationSubmittedDate;
       }

       let calculatedAorDate = currentFormData.aorDate;
       if (!isDateValid(calculatedAorDate) && isDateValid(calculatedAppSubmittedDate)) {
           updates.aorDate = addDaysSafely(calculatedAppSubmittedDate, 2);
           calculatedAorDate = updates.aorDate;
       }

       let calculatedBilDate = currentFormData.biometricsInstructionLetterDate;
       if (!isDateValid(calculatedBilDate) && isDateValid(calculatedAorDate)) {
           updates.biometricsInstructionLetterDate = addDaysSafely(calculatedAorDate, 2);
           calculatedBilDate = updates.biometricsInstructionLetterDate;
       }

       let calculatedBiometricsSubmittedDate = currentFormData.biometricsSubmittedDate;
       if (!isDateValid(calculatedBiometricsSubmittedDate) && isDateValid(calculatedBilDate)) {
           updates.biometricsSubmittedDate = addDaysSafely(calculatedBilDate, 4);
       }

       let calculatedBgCompletionDate = currentFormData.backgroundCheckStartDate;
       if (!isDateValid(calculatedBgCompletionDate) && isDateValid(calculatedAorDate)) {
           updates.backgroundCheckStartDate = addMonthsSafely(calculatedAorDate, 2.5);
           calculatedBgCompletionDate = updates.backgroundCheckStartDate;
       }

       let calculatedFinalDecisionDate = currentFormData.finalDecisionDate;
       if (!isDateValid(calculatedFinalDecisionDate)) {
           if (securityCheck && isDateValid(calculatedAorDate)) {
               updates.finalDecisionDate = addMonthsSafely(calculatedAorDate, 12);
               calculatedFinalDecisionDate = updates.finalDecisionDate;
           } else if (!securityCheck && isDateValid(calculatedBgCompletionDate)) {
                updates.finalDecisionDate = addDaysSafely(calculatedBgCompletionDate, 4);
                calculatedFinalDecisionDate = updates.finalDecisionDate;
           }
       }

       let calculatedPortal1Date = currentFormData.portal1Date;
       if (!isDateValid(calculatedPortal1Date) && isDateValid(calculatedFinalDecisionDate)) {
           updates.portal1Date = addWeeksSafely(calculatedFinalDecisionDate, 1);
           calculatedPortal1Date = updates.portal1Date;
       }

       let calculatedPortal2Date = currentFormData.portal2Date;
       if (!isDateValid(calculatedPortal2Date) && isDateValid(calculatedPortal1Date)) {
           updates.portal2Date = addWeeksSafely(calculatedPortal1Date, 1);
           calculatedPortal2Date = updates.portal2Date;
       }

       if (!isDateValid(currentFormData.eCoprDate) && isDateValid(calculatedPortal2Date)) {
           updates.eCoprDate = addWeeksSafely(calculatedPortal2Date, 3);
       }

       return Object.entries(updates)
           .filter(([_, value]) => value !== null && value !== undefined)
           .reduce((obj, [key, value]) => { obj[key] = value; return obj; }, {});
    }, []);


    useEffect(() => {
        if (enableAutofill && isDateValid(formData.itaDate)) {
            const updates = calculateDependentDates(formData);
            if (Object.keys(updates).length > 0) {
                console.log("Auto-filling dates:", updates);
                Object.entries(updates).forEach(([fieldName, fieldValue]) => {
                    if (!formData[fieldName]) { // Only fill if currently empty
                        onChange({ target: { name: fieldName, value: fieldValue } });
                    }
                });
            }
        }
    }, [
        formData,
        enableAutofill,
        onChange,
        calculateDependentDates
    ]);

    // --- Rendering Functions ---
    const renderDateField = (name, label, views = ["year", "month", "day"], required = false) => (
        <DatePicker
            label={`${label}${required ? " *" : ""}`}
            // --- MODIFIED: Use parseIncomingDate helper ---
            value={parseIncomingDate(formData[name])}
            // --- End Modification ---
            onChange={(date) => handleDateChange(name, date)} // Uses the modified handler
            emptyLabel="Select a date"
            views={views}
            maxDate={new Date()}
            slotProps={{
                textField: { fullWidth: true, size: "small", margin: "dense", required: required, name: name },
            }}
            
        />
    );

    // --- JSX Output ---
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ /* Box styling remains the same */
                mt: 2, p: 1, width: "95%", maxWidth: 600, border: "1px solid rgba(224, 224, 224, 0.5)",
                borderRadius: "12px", backgroundColor: "rgba(255, 255, 255, 0.80)",
                backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", mx: "auto"
            }}>
                <Typography variant="subtitle1" gutterBottom align="center">Immigration Timeline</Typography>
                <Grid container spacing={1} alignItems="center">
                    {/* Stream, VO, EE/PNP Switches */}
                    <Grid item xs={12} sm={5}>
                       <FormControl fullWidth margin="dense" size="small" required>
                           <InputLabel id="applicationStream-label">Stream *</InputLabel>
                           <Select labelId="applicationStream-label" name="applicationStream" value={formData.applicationStream || ""} label="Stream *" onChange={handleInputChange}>
                               {ApplicationStreamEnum.map((stream) => <MenuItem key={stream} value={stream}>{stream}</MenuItem>)}
                           </Select>
                       </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                       <FormControl fullWidth margin="dense" size="small">
                           <InputLabel id="voOffice-label">Visa Office (VO)</InputLabel>
                           <Select
                               labelId="voOffice-label"
                               name="voOffice"
                               value={formData.voOffice || ""}
                               label="Visa Office (VO)"
                               onChange={handleInputChange}
                           >
                               {VisaOfficeEnum.map((vo) => (
                                   <MenuItem key={vo} value={vo}>
                                       {vo}
                                   </MenuItem>
                               ))}
                           </Select>
                       </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4} container spacing={0} alignItems="center" justifyContent="center" sx={{ pt: { xs: 1, sm: 0 } }}>
                        <Grid item xs={6} sm={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}><FormControlLabel control={<Switch checked={formData.isExpressEntry || false} onChange={handleSwitchChange} name="isExpressEntry" size="small"/>} label="Express Entry?" sx={{ m: 0 }} /></Grid>
                        <Grid item xs={6} sm={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}><FormControlLabel control={<Switch checked={formData.isPnp || false} onChange={handleSwitchChange} name="isPnp" size="small"/>} label="PNP?" sx={{ m: 0 }}/></Grid>
                    </Grid>

                    {/* ITA Date and Conditional NOC */}
                    <Grid item xs={12} sm={formData.isExpressEntry ? 6 : 12}>
                        {renderDateField("itaDate", "ITA Date", ["year", "month", "day"], true)}
                    </Grid>
                    {formData.isExpressEntry && (
                       <Grid item xs={12} sm={6}><TextField fullWidth margin="dense" size="small" label="NOC Code" name="nocCode" value={formData.nocCode || ""} onChange={handleInputChange}/></Grid>
                    )}

                    {/* PNP Specific Fields */}
                    {formData.isPnp && (
                        <>
                           <Grid item xs={12} sm={6}><FormControl fullWidth margin="dense" size="small"><InputLabel id="pnpProvince-label">PNP Province</InputLabel><Select labelId="pnpProvince-label" name="pnpProvince" value={formData.pnpProvince || ""} label="PNP Province" onChange={handleInputChange}>{ProvincesEnum.map((province) => <MenuItem key={province} value={province}>{province}</MenuItem>)}</Select></FormControl></Grid>
                           <Grid item xs={12} sm={6}>{renderDateField("pnpNominationDate", "PNP Nomination Date")}</Grid>
                        </>
                    )}

                    {/* Security Check Section */}
                    <Grid item xs={12} sm={formData.securityCheck ? 6 : 12} sx={{ display: 'flex', alignItems: 'center' }}>
                       <FormControlLabel control={<Switch checked={formData.securityCheck || false} onChange={handleSwitchChange} name="securityCheck" size="small"/>} label="Security Check Required?" sx={{ m: 0, display: 'flex', justifyContent: 'flex-start' }}/>
                    </Grid>
                    {formData.securityCheck && (
                       <Grid item xs={12} sm={6}><TextField fullWidth margin="dense" size="small" label="Birth Country" name="birthCountry" value={formData.birthCountry || ""} onChange={handleInputChange}/></Grid>
                    )}

                    {/* Auto-fill Toggle */}
                    <Grid item xs={12} sx={{ mt: 1, mb: -0.5 }}>
                        <FormControlLabel control={<Switch checked={enableAutofill} onChange={(e) => setEnableAutofill(e.target.checked)} name="enableAutofill" size="small"/>} label="Enable Auto-fill Dates" sx={{ display: 'flex', justifyContent: 'flex-start' }}/>
                        <FormHelperText sx={{ ml: 4, mt: -0.5 }}> If enabled, milestone dates below will be estimated based on ITA Date if left blank. Clears automatically if you manually clear a milestone date.</FormHelperText> {/* Updated helper text */}
                    </Grid>

                    {/* Core Milestones Section */}
                    <Grid item xs={12}><Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Core Milestones {enableAutofill ? "(auto-filled if blank)" : ""}</Typography></Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("applicationSubmittedDate", "Application Submitted", ["year", "month", "day"], true)}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("aorDate", "AOR Date")}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("biometricsInstructionLetterDate", "BIL Date")}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("biometricsSubmittedDate", "Biometrics Done")}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("backgroundCheckStartDate", "BG Completion Date")}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("finalDecisionDate", "Final Decision")}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("portal1Date", "Portal 1 Email")}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("portal2Date", "Portal 2 Credentials")}</Grid>
                    <Grid item xs={12} sm={6} md={4}>{renderDateField("eCoprDate", "eCoPR Received")}</Grid>

                    {/* Optional Notes Field */}
                    <Grid item xs={12}><TextField fullWidth margin="dense" size="small" label="Notes (Optional)" name="notes" value={formData.notes || ""} onChange={handleInputChange} multiline rows={2}/></Grid>
                </Grid>
            </Box>
        </LocalizationProvider>
    );
};

export default TimelineForm;